import React, { useState, useEffect } from 'react';
import { valoresAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, DollarSign, Power, Tv, Wifi, Phone, Smartphone, Home, Building2, CheckCircle2, XCircle } from 'lucide-react';

const TIPOS = [
  { value: 'TV', label: 'TV', icon: Tv, color: 'bg-purple-100 text-purple-800' },
  { value: 'INTERNET', label: 'Internet', icon: Wifi, color: 'bg-red-100 text-red-800' },
  { value: 'FIXO', label: 'Fixo', icon: Phone, color: 'bg-green-100 text-green-800' },
  { value: 'MOVEL', label: 'Movel', icon: Smartphone, color: 'bg-orange-100 text-orange-800' },
  { value: 'A LA CARTE', label: 'A La Carte', icon: Tv, color: 'bg-purple-100 text-purple-800' },
  { value: 'PONTOS EXTRA', label: 'Pontos Extra', icon: Plus, color: 'bg-cyan-100 text-cyan-800' },
  { value: 'PONTOS ADICIONAIS', label: 'Ponto Adicional (TV)', icon: Plus, color: 'bg-amber-100 text-amber-800' },
];

const Valores = () => {
  const [valores, setValores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [categoriaAtiva, setCategoriaAtiva] = useState('Residencial');
  const [statusAba, setStatusAba] = useState('ativos');

  const [formData, setFormData] = useState({
    produto: '',
    tipo: 'TV',
    nome_plano: '',
    valor: '',
    valor_boleto: '',
    categoria: 'Residencial',
    permite_dependentes: false,
  });

  useEffect(() => { carregarValores(); }, []);

  const carregarValores = async () => {
    setLoading(true);
    try {
      const response = await valoresAPI.listar();
      setValores(response.data);
    } catch (error) {
      toast.error('Erro ao carregar valores');
    } finally {
      setLoading(false);
    }
  };

  const abrirNovo = () => {
    setEditando(null);
    setFormData({ produto: '', tipo: 'TV', nome_plano: '', valor: '', valor_boleto: '', categoria: categoriaAtiva, permite_dependentes: false });
    setModalAberto(true);
  };

  const abrirEditar = (valor) => {
    setEditando(valor);
    setFormData({
      produto: valor.produto,
      tipo: valor.tipo,
      nome_plano: valor.nome_plano,
      valor: String(valor.valor),
      valor_boleto: valor.valor_boleto != null ? String(valor.valor_boleto) : '',
      categoria: valor.categoria || 'Residencial',
      permite_dependentes: valor.permite_dependentes === true,
    });
    setModalAberto(true);
  };

  const handleSalvar = async () => {
    if (!formData.nome_plano.trim() || !formData.valor) {
      toast.error('Preencha Nome do Plano e Valor');
      return;
    }

    const valorNum = parseFloat(formData.valor.replace(',', '.'));
    if (isNaN(valorNum) || valorNum <= 0) {
      toast.error('Valor invalido');
      return;
    }
    let valorBoleto = null;
    if (formData.categoria === 'Empresarial' && String(formData.valor_boleto || '').trim()) {
      valorBoleto = parseFloat(String(formData.valor_boleto).replace(',', '.'));
      if (isNaN(valorBoleto) || valorBoleto <= 0) {
        toast.error('Valor do boleto invalido');
        return;
      }
    }

    setSalvando(true);
    try {
      const payload = { ...formData, valor: valorNum, valor_boleto: valorBoleto };
      if (editando) {
        await valoresAPI.atualizar(editando.id, payload);
        toast.success('Valor atualizado!');
      } else {
        await valoresAPI.criar(payload);
        toast.success('Valor criado!');
      }
      setModalAberto(false);
      carregarValores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const handleToggleAtivo = async (valor) => {
    try {
      await valoresAPI.atualizar(valor.id, { ativo: !valor.ativo });
      toast.success(valor.ativo ? 'Plano inativado' : 'Plano ativado');
      carregarValores();
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const handleExcluir = async (valor) => {
    if (!window.confirm(`Excluir "${valor.nome_plano}"?`)) return;
    try {
      await valoresAPI.excluir(valor.id);
      toast.success('Valor excluido!');
      carregarValores();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const getTipoInfo = (tipo) => TIPOS.find(t => t.value === tipo) || TIPOS[0];

  const valoresCategoriaTodos = valores.filter(v => (v.categoria || 'Residencial') === categoriaAtiva);
  const countAtivos = valoresCategoriaTodos.filter(v => v.ativo !== false).length;
  const countInativos = valoresCategoriaTodos.length - countAtivos;
  const valoresCategoria = valoresCategoriaTodos.filter(v => (statusAba === 'ativos' ? v.ativo !== false : v.ativo === false));
  const valoresFiltrados = filtroTipo === 'todos' ? valoresCategoria : valoresCategoria.filter(v => v.tipo === filtroTipo);

  const countResidencial = valores.filter(v => (v.categoria || 'Residencial') === 'Residencial').length;
  const countEmpresarial = valores.filter(v => (v.categoria || 'Residencial') === 'Empresarial').length;

  const formatarValor = (v) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  };

  return (
    <div className="space-y-6" data-testid="valores-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="h-7 w-7 text-red-600" />
            Tabela de Valores
          </h1>
          <p className="text-gray-500 text-sm mt-1">Gerencie os valores dos planos separados por categoria</p>
        </div>
        <Button className="bg-red-600 hover:bg-red-700" onClick={abrirNovo} data-testid="novo-valor-btn">
          <Plus className="mr-2 h-4 w-4" />
          Novo Valor
        </Button>
      </div>

      {/* Abas Residencial / Empresarial */}
      <div className="flex gap-0 border-b border-gray-200">
        <button
          data-testid="tab-residencial"
          onClick={() => { setCategoriaAtiva('Residencial'); setFiltroTipo('todos'); }}
          className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-all ${
            categoriaAtiva === 'Residencial'
              ? 'border-red-600 text-red-700 bg-red-50/50'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Home className="h-4 w-4" />
          Residencial
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            categoriaAtiva === 'Residencial' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
          }`}>{countResidencial}</span>
        </button>
        <button
          data-testid="tab-empresarial"
          onClick={() => { setCategoriaAtiva('Empresarial'); setFiltroTipo('todos'); }}
          className={`flex items-center gap-2 px-6 py-3 text-sm font-semibold border-b-2 transition-all ${
            categoriaAtiva === 'Empresarial'
              ? 'border-red-600 text-red-700 bg-red-50/50'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Building2 className="h-4 w-4" />
          Empresarial
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            categoriaAtiva === 'Empresarial' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
          }`}>{countEmpresarial}</span>
        </button>
      </div>

      {/* Abas Ativos / Inativos */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="tabs-status-valores">
        <button
          data-testid="tab-ativos"
          onClick={() => { setStatusAba('ativos'); setFiltroTipo('todos'); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
            statusAba === 'ativos'
              ? 'bg-green-600 text-white border-green-600 shadow'
              : 'border-gray-300 text-gray-500 hover:bg-green-50 hover:text-green-700'
          }`}
        >
          <CheckCircle2 className="h-4 w-4" />
          Ativos
          <span className={`text-xs px-2 py-0.5 rounded-full ${statusAba === 'ativos' ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`} data-testid="count-ativos">{countAtivos}</span>
        </button>
        <button
          data-testid="tab-inativos"
          onClick={() => { setStatusAba('inativos'); setFiltroTipo('todos'); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
            statusAba === 'inativos'
              ? 'bg-red-600 text-white border-red-600 shadow'
              : 'border-gray-300 text-gray-500 hover:bg-red-50 hover:text-red-700'
          }`}
        >
          <XCircle className="h-4 w-4" />
          Inativos
          <span className={`text-xs px-2 py-0.5 rounded-full ${statusAba === 'inativos' ? 'bg-white/20' : 'bg-gray-100 text-gray-500'}`} data-testid="count-inativos">{countInativos}</span>
        </button>
        <span className="text-xs text-gray-500 ml-2">
          {statusAba === 'ativos' ? 'Planos disponíveis para venda' : 'Planos ocultos na Nova Venda (podem ser reativados)'}
        </span>
      </div>

      {/* Filtro por tipo */}
      <div className="flex gap-2 flex-wrap">
        <Button variant={filtroTipo === 'todos' ? 'default' : 'outline'} size="sm" onClick={() => setFiltroTipo('todos')}>
          Todos ({valoresCategoria.length})
        </Button>
        {TIPOS.map(tipo => {
          const count = valoresCategoria.filter(v => v.tipo === tipo.value).length;
          if (count === 0) return null;
          const Icon = tipo.icon;
          return (
            <Button key={tipo.value} variant={filtroTipo === tipo.value ? 'default' : 'outline'} size="sm" onClick={() => setFiltroTipo(tipo.value)}>
              <Icon className="mr-1 h-4 w-4" />
              {tipo.label} ({count})
            </Button>
          );
        })}
      </div>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-red-600" /></div>
          ) : valoresFiltrados.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg" data-testid="valores-vazio">
              <DollarSign className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">Nenhum plano {categoriaAtiva.toLowerCase()} {statusAba === 'ativos' ? 'ativo' : 'inativo'}</p>
              {statusAba === 'ativos' && (
                <Button className="mt-3 bg-red-600 hover:bg-red-700" onClick={abrirNovo} size="sm">
                  <Plus className="mr-1 h-4 w-4" /> Adicionar Plano
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left p-3 font-semibold">Tipo</th>
                    <th className="text-left p-3 font-semibold">Plano</th>
                    <th className="text-right p-3 font-semibold">Valor</th>
                    <th className="text-center p-3 font-semibold">Status</th>
                    <th className="text-right p-3 font-semibold">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {valoresFiltrados.map(valor => {
                    const tipoInfo = getTipoInfo(valor.tipo);
                    const Icon = tipoInfo.icon;
                    return (
                      <tr key={valor.id} className={`border-b hover:bg-gray-50 transition-colors ${!valor.ativo ? 'opacity-50' : ''}`}>
                        <td className="p-3">
                          <Badge className={tipoInfo.color}>
                            <Icon className="h-3 w-3 mr-1" />
                            {tipoInfo.label}
                          </Badge>
                        </td>
                        <td className="p-3 font-medium">{valor.nome_plano}</td>
                        <td className="p-3 text-right font-bold text-red-700">{formatarValor(valor.valor)}{valor.valor_boleto != null && <span className="block text-[11px] font-medium text-gray-500" data-testid={`valor-boleto-${valor.id}`}>boleto {formatarValor(valor.valor_boleto)}</span>}</td>
                        <td className="p-3 text-center">
                          <Badge className={valor.ativo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {valor.ativo ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="outline" onClick={() => abrirEditar(valor)} data-testid={`editar-valor-${valor.id}`}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleToggleAtivo(valor)} className={valor.ativo ? 'text-amber-600' : 'text-green-600'} title={valor.ativo ? 'Inativar plano' : 'Reativar plano'} data-testid={`toggle-ativo-${valor.id}`}>
                              <Power className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleExcluir(valor)} data-testid={`excluir-valor-${valor.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={modalAberto} onOpenChange={setModalAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editando ? 'Editar Valor' : 'Novo Valor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Categoria <span className="text-red-500">*</span></Label>
              <div className="flex gap-0 mt-1.5 rounded-lg overflow-hidden border border-gray-200" data-testid="valor-categoria-toggle">
                <button
                  type="button"
                  data-testid="valor-cat-residencial"
                  className={`flex-1 px-4 py-2 text-sm font-semibold transition-all ${
                    formData.categoria === 'Residencial'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                  onClick={() => setFormData({...formData, categoria: 'Residencial'})}
                >
                  Residencial
                </button>
                <button
                  type="button"
                  data-testid="valor-cat-empresarial"
                  className={`flex-1 px-4 py-2 text-sm font-semibold transition-all ${
                    formData.categoria === 'Empresarial'
                      ? 'bg-red-600 text-white'
                      : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                  onClick={() => setFormData({...formData, categoria: 'Empresarial'})}
                >
                  Empresarial
                </button>
              </div>
            </div>
            <div>
              <Label>Tipo <span className="text-red-500">*</span></Label>
              <Select value={formData.tipo} onValueChange={v => setFormData({...formData, tipo: v})}>
                <SelectTrigger className="mt-1" data-testid="valor-tipo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{formData.tipo === 'PONTOS ADICIONAIS' ? 'Tipo do ponto adicional' : 'Nome do Plano'} <span className="text-red-500">*</span></Label>
              <Input value={formData.nome_plano} onChange={e => setFormData({...formData, nome_plano: e.target.value.toUpperCase()})} placeholder={formData.tipo === 'PONTOS ADICIONAIS' ? 'Ex: HD, 4K, HD COM GRAVADOR' : 'Ex: BOX HD'} className="mt-1" data-testid="valor-plano-input" />
              {formData.tipo === 'PONTOS ADICIONAIS' && (
                <div className="mt-2 space-y-1.5" data-testid="pontos-sugestoes">
                  <div className="flex flex-wrap gap-1.5">
                    {['HD', '4K', 'HD COM GRAVADOR', '4K COM GRAVADOR', 'BOX ADICIONAL'].map(s => (
                      <button key={s} type="button" onClick={() => setFormData({...formData, nome_plano: s})} className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${formData.nome_plano === s ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`} data-testid={`ponto-sugestao-${s.toLowerCase().replace(/\s/g, '-')}`}>{s}</button>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-500">Aparece para o vendedor em TV → "Tipo do ponto adicional"; o valor unitário vem daqui (não é digitado na venda).</p>
                </div>
              )}
            </div>
            <div className={formData.categoria === 'Empresarial' ? 'grid grid-cols-2 gap-3' : ''}>
              <div>
                <Label>{formData.categoria === 'Empresarial' ? 'Valor Débito Automático (R$)' : 'Valor (R$)'} <span className="text-red-500">*</span></Label>
                <Input value={formData.valor} onChange={e => setFormData({...formData, valor: e.target.value})} placeholder="Ex: 89.90" className="mt-1" data-testid="valor-preco-input" />
              </div>
              {formData.categoria === 'Empresarial' && (
                <div>
                  <Label>Valor Boleto (R$)</Label>
                  <Input value={formData.valor_boleto} onChange={e => setFormData({...formData, valor_boleto: e.target.value})} placeholder="Igual ao débito se vazio" className="mt-1" data-testid="valor-preco-boleto-input" />
                </div>
              )}
            </div>
            {formData.categoria === 'Empresarial' && (
              <p className="text-[11px] text-gray-500 -mt-2">Na venda, o valor do plano é preenchido automaticamente conforme a forma de pagamento (débito ou boleto).</p>
            )}
            {formData.tipo === 'MOVEL' && (
              <label className="flex items-start gap-2 p-3 rounded-lg border border-purple-300/60 bg-purple-50/40 dark:bg-purple-950/10 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 accent-purple-600"
                  checked={!!formData.permite_dependentes}
                  onChange={e => setFormData({...formData, permite_dependentes: e.target.checked})}
                  data-testid="valor-permite-dependentes"
                />
                <span className="text-sm">
                  <span className="font-semibold">Plano Pós-Pago ou superior</span>
                  <span className="block text-xs text-gray-500">Libera a seção "Dependentes" (linhas adicionais) na Nova Venda</span>
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalAberto(false)}>Cancelar</Button>
            <Button onClick={handleSalvar} disabled={salvando} className="bg-red-600 hover:bg-red-700" data-testid="salvar-valor-btn">
              {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Valores;
